// 管理者用：講師の通常授業予定（曜日×講）設定ページ

import React, { useEffect, useState } from "react";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import { formatDate } from "../../utils/formatDate";

const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
const periods = [1, 2, 3, 4, 5, 6, 7];

const AdminTeacherWeeklyShifts = () => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    dayPeriods: {} // { 0: [1,2,3], 1: [2,4], ... }
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    getRequest("teachers?excludeRetired=true").then(data => {
      setTeachers(data.teachers || []);
    });
  }, []);

  const fetchShifts = async (uid) => {
    const res = await getRequest(`teacherWeeklyShifts?uid=${uid}`);
    setShifts(res.shifts);
  };

  const handleSelect = (uid) => {
    setSelectedTeacher(uid);
    setFormData({ start_date: "", end_date: "", dayPeriods: {} });
    setEditingId(null);
    fetchShifts(uid);
  };

  const handleCheckboxToggle = (day, period) => {
    setFormData(prev => {
      const newDayPeriods = { ...prev.dayPeriods };
      const current = newDayPeriods[day] || [];
      if (current.includes(period)) {
        newDayPeriods[day] = current.filter(p => p !== period);
      } else {
        newDayPeriods[day] = [...current, period];
      }
      return { ...prev, dayPeriods: newDayPeriods };
    });
  };
  
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { start_date, end_date, dayPeriods } = formData;
    if (!start_date) {
      toast.error("開始日は必須です");
      return;
    }
    const allRequests = [];
    Object.entries(dayPeriods).forEach(([day, periods]) => {
      if (periods.length === 0) return;
      periods.forEach(period => {
    const payload = {
      teacherid: selectedTeacher,
          day_of_week: Number(day),
          period: Number(period),
      start_date,
      end_date: end_date === "" ? null : end_date
    };
        allRequests.push(postRequest("teacherWeeklyShifts", payload));
      });
    });

    try {
      await Promise.all(allRequests);
      toast.success("一括登録しました");
      setFormData({ start_date: "", end_date: "", dayPeriods: {} });
      fetchShifts(selectedTeacher);
    } catch (err) {
      console.error("送信エラー", err);
      toast.error("登録に失敗しました");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("本当に削除しますか？")) return;
    const res = await deleteRequest(`teacherWeeklyShifts/${id}`);
    if (res.ok) {
      toast.success("削除しました");
      fetchShifts(selectedTeacher);
    } else {
      const data = await res.json();
      toast.error(data.error || "削除に失敗しました");
    }
  };

  return (
    <div className="p-6 pt-20 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">講師の通常授業予定（曜日別まとめて設定）</h1>

      <div className="mb-4">
        <select className="border p-2 w-full" onChange={(e) => handleSelect(e.target.value)} value={selectedTeacher || ""}>
          <option value="">講師を選択</option>
          {teachers.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {selectedTeacher && (
        <div className="bg-white p-4 rounded shadow mb-6">
          <h2 className="text-lg font-semibold mb-2">曜日ごとの出勤可能講をまとめて登録</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {weekdays.map((label, i) => (
              <div key={i} className="border rounded p-2">
                <h4 className="font-semibold mb-2">{label}曜日</h4>
                <div className="flex flex-wrap gap-2">
                  {periods.map(p => (
                    <label key={p} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={(formData.dayPeriods[i] || []).includes(p)}
                        onChange={() => handleCheckboxToggle(i, p)}
                        className="mr-1"
                      />
                      {p}講
                    </label>
              ))}
                </div>
              </div>
              ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <input type="date" name="start_date" value={formData.start_date} onChange={e => handleChange(e)} className="p-2 border rounded" placeholder="開始日" />
            <input type="date" name="end_date" value={formData.end_date} onChange={e => handleChange(e)} className="p-2 border rounded" placeholder="終了日（任意）" />
          </div>
          <button onClick={handleSubmit} className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
            一括登録
          </button>
        </div>
      )}

      {selectedTeacher && (
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-lg font-semibold mb-2">登録済みシフト</h2>
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 border">曜日</th>
                <th className="p-2 border">講</th>
                <th className="p-2 border">開始</th>
                <th className="p-2 border">終了</th>
                <th className="p-2 border">操作</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map(s => (
                <tr key={s.id} className="border">
                  <td className="p-2 border">{weekdays[s.day_of_week]}</td>
                  <td className="p-2 border">{s.period ?? "すべて"}</td>
                  <td className="p-2 border">{formatDate(s.start_date)}</td>
                  <td className="p-2 border">{formatDate(s.end_date)}</td>
                  <td className="p-2 border flex gap-2">
                    <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTeacherWeeklyShifts;
